import { Controller } from '@hotwired/stimulus'
import { debounce } from 'lodash'
import flatpickr from 'flatpickr'

class DatepickerController extends Controller {
  static values = {
    options: Object,
    onChangeFunction: String,
    onMonthChangeFunction: String,
    onYearChangeFunction: String,
    jumpToDate: { type: String, default: null },
    readonlyYear: { type: Boolean, default: false },
  }

  connect() {
    this.debouncedOnChange = debounce(this.handleChange.bind(this), 100)
    this.debouncedOnMonthChange = debounce(this.handleMonthChange.bind(this), 100)
    this.debouncedOnYearChange = debounce(this.handleYearChange.bind(this), 100)

    // See all configuration options at https://flatpickr.js.org/options
    const defaultOptions = {
      altInput: true,
      locale: {
        weekdays: {
          shorthand: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
          longhand: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        },
        months: {
          shorthand: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          longhand: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        },
      },
      prevArrow: `
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 17 17">
          <path d="M11.5 2.5L6 8l5.5 5.5L10 15L3 8l7-7z" fill="currentColor"/>
        </svg>
      `,
      nextArrow: `
        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 17 17">
          <path d="M5.5 2.5L11 8l-5.5 5.5L7 15l7-7-7-7z" fill="currentColor"/>
        </svg>
      `,

    }

    const options = { ...defaultOptions, ...this.optionsValue }

    options.onChange = this.debouncedOnChange
    options.onMonthChange = this.debouncedOnMonthChange
    options.onYearChange = this.debouncedOnYearChange

    options.onReady = (selectedDates, dateStr, instance) => {
      if (this.readonlyYearValue) {
        this.makeYearReadonly()
      }
      if (!this.element.value && 'defaultDate' in this.optionsValue) {
        this.element.value = this.optionsValue.defaultDate
      }

      this.element.dispatchEvent(new CustomEvent('datepicker:ready', {
        detail: { selectedDates, dateStr, instance },
        bubbles: true,
      }))
    }

    this.flatpickr = flatpickr(this.element, options)

    if (this.jumpToDateValue) {
      this.flatpickr.jumpToDate(this.jumpToDateValue)
      this.flatpickr.redraw()
    }
  }

  handleChange = (selectedDates, dateStr, instance) => {
    this.element.dispatchEvent(new CustomEvent('datepicker:change', {
      detail: { selectedDates, dateStr, instance },
      bubbles: true,
    }))

    if (this.hasOnChangeFunctionValue) {
      const handlerName = this.onChangeFunctionValue
      if (handlerName && typeof window[handlerName] === 'function') {
        window[handlerName](selectedDates, dateStr, instance)
      }
    }
  }

  handleMonthChange = (selectedDates, dateStr, instance) => {
    this.element.dispatchEvent(new CustomEvent('datepicker:monthChange', {
      detail: { selectedDates, dateStr, instance },
      bubbles: true,
    }))

    if (this.hasOnMonthChangeFunctionValue) {
      const handlerName = this.onMonthChangeFunctionValue
      if (handlerName && typeof window[handlerName] === 'function') {
        window[handlerName](selectedDates, dateStr, instance)
      }
    }
  }

  handleYearChange = (selectedDates, dateStr, instance) => {
    this.element.dispatchEvent(new CustomEvent('datepicker:yearChange', {
      detail: { selectedDates, dateStr, instance },
      bubbles: true,
    }))

    if (this.hasOnYearChangeValue) {
      const handlerName = this.onYearChangeValue
      if (handlerName && typeof window[handlerName] === 'function') {
        window[handlerName](selectedDates, dateStr, instance)
      }
    }
  }

  disconnect() {
    if (this.debouncedOnChange) {
      this.debouncedOnChange.cancel()
    }
    if (this.debouncedOnMonthChange) {
      this.debouncedOnMonthChange.cancel()
    }
    if (this.debouncedOnYearChange) {
      this.debouncedOnYearChange.cancel()
    }

    if (this.flatpickr) {
      this.flatpickr.destroy()
    }
  }

  makeYearReadonly() {
    const yearInput = this.element.parentNode.querySelector('input.numInput.cur-year')
    if (yearInput) {
      yearInput.setAttribute('readonly', 'readonly')
    }
  }

  clear() {
    this.flatpickr.clear()
  }

  open() {
    this.flatpickr.open()
  }

  close() {
    this.flatpickr.close()
  }

  setOption(key, value) {
    this.flatpickr.set(key, value)
  }

  setDate(date) {
    this.flatpickr.setDate(date)
  }

  getSelectedDates() {
    return this.flatpickr.selectedDates
  }

  getSelectedDate() {
    return this.flatpickr.selectedDates[0]
  }
}

export default DatepickerController
